import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Users,
    UserCheck,
    UserX,
    Clock,
    TrendingUp,
    AlertTriangle,
    RefreshCw,
    ChevronLeft,
    Activity,
    Timer,
    Zap,
    AlertCircle,
    XCircle,
    UserMinus,
    ChevronDown
} from 'lucide-react';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { toast } from '../../../utils/toast';
import { formatIndianNumber } from '../../../utils/amountFormatter';

const AttendanceKpiDashboard = ({ onBack }) => {
    // 'custom' = use manual date inputs
    // '7', '15', '30', etc. = preset ranges
    const [timeFilter, setTimeFilter] = useState('today');

    // Default to today
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const [isRefreshing, setIsRefreshing] = useState(false);

    // Update dates based on filter selection
    useEffect(() => {
        if (timeFilter === 'custom') return;

        const end = new Date();
        const start = new Date();

        if (timeFilter !== 'today') {
            const daysToSubtract = parseInt(timeFilter) - 1;
            start.setDate(end.getDate() - daysToSubtract);
        }

        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
    }, [timeFilter]);

    // Fetch dashboard data using React Query
    const {
        data: dashboardData,
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['attendance-kpi-dashboard', startDate, endDate],
        queryFn: async () => {
            const token = localStorage.getItem('auth_token');
            const url = `${process.env.REACT_APP_API_BASE_URL}/attendance/dashboard?startDate=${startDate}&endDate=${endDate}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch dashboard data');
            }
            const result = await response.json();
            if (result.success) {
                return result.data;
            } else {
                throw new Error(result.message || 'Failed to fetch dashboard data');
            }
        },
        staleTime: 0,
        refetchOnMount: true
    });

    // Handle refresh
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refetch();
            toast.success('Dashboard refreshed successfully');
        } catch (error) {
            toast.error('Failed to refresh dashboard');
        } finally {
            setIsRefreshing(false);
        }
    };

    // Handle filter change
    const handleFilterChange = (e) => {
        setTimeFilter(e.target.value);
    };

    // Format date for display
    const formatDisplayDate = (startStr, endStr) => {
        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        const start = new Date(startStr).toLocaleDateString('en-US', options);
        const end = new Date(endStr).toLocaleDateString('en-US', options);
        return start === end ? start : `${start} - ${end}`;
    };

    const KpiCard = ({ icon: Icon, title, value, color = 'blue', subtitle, trend }) => {
        const badgeColors = {
            blue: "bg-blue-50 text-blue-600",
            green: "bg-emerald-50 text-emerald-600",
            purple: "bg-purple-50 text-purple-600",
            red: "bg-rose-50 text-rose-600",
            amber: "bg-amber-50 text-amber-600",
        };
        const selectedBadge = badgeColors[color] || badgeColors.blue;

        return (
            <div className="bg-white rounded-2xl p-3.5 sm:p-4 border border-slate-100 shadow-2xs hover:shadow-md transition-all duration-200 flex items-center gap-3.5">
                {Icon && (
                    <div className={`w-9 h-9 rounded-full ${selectedBadge} flex items-center justify-center shrink-0 shadow-2xs`}>
                        <Icon className="w-4.5 h-4.5" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-semibold text-slate-500 truncate tracking-wide">
                        {title}
                    </h3>
                    <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-xl sm:text-2xl font-bold text-slate-900 leading-none">
                            {value}
                        </span>
                        {subtitle && (
                            <span className="text-[11px] font-medium text-slate-400 truncate">
                                {subtitle}
                            </span>
                        )}
                        {trend && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${trend.color}`}>
                                {trend.label}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Section Header Component
    const SectionHeader = ({ icon: Icon, title, color }) => (
        <div className="flex items-center gap-3 mb-6">
            <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
    );

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-red-900 mb-2">
                            Failed to Load Dashboard
                        </h3>
                        <p className="text-red-700 mb-4">{error.message}</p>
                        <button
                            onClick={handleRefresh}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            aria-label="Try again to load dashboard"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    aria-label="Back to previous page"
                                >
                                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                                </button>
                            )}
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                                    Attendance KPI Dashboard
                                </h1>
                                <p className="text-sm text-gray-500">
                                    {startDate && endDate
                                        ? formatDisplayDate(startDate, endDate)
                                        : 'Select Date Range'}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            {/* Time Range Filter */}
                            <div className="relative">
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                                <select
                                    value={timeFilter}
                                    onChange={handleFilterChange}
                                    className="appearance-none bg-gray-50 pl-4 pr-10 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent cursor-pointer"
                                >
                                    <option value="custom">Custom Range</option>
                                    <option value="today">Today</option>
                                    <option value="7">Last 7 Days</option>
                                    <option value="15">Last 15 Days</option>
                                    <option value="30">Last 1 Month</option>
                                    <option value="90">Last 3 Months</option>
                                    <option value="180">Last 6 Months</option>
                                    <option value="365">Last 1 Year</option>
                                </select>
                            </div>

                            {/* Date Inputs - Only shown when Custom Range is selected */}
                            {timeFilter === 'custom' && (
                                <div className="flex-shrink-0">
                                    <DateRangeFilter
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(range) => {
                                            setStartDate(range.startDate);
                                            setEndDate(range.endDate);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {isLoading ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
                        <div className="flex flex-col items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                            <p className="text-gray-600 font-medium">Loading dashboard data...</p>
                        </div>
                    </div>
                ) : dashboardData ? (
                    <>
                        {/* 🟢 Workforce Snapshot */}
                        <div className="p-6">
                            <SectionHeader
                                icon={Users}
                                title="Workforce Snapshot"
                                color="bg-green-500"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard
                                    icon={Users}
                                    title="Total Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.totalEmployees)}
                                    color="bg-[#0F2942]"
                                />
                                <KpiCard
                                    icon={UserCheck}
                                    title="Active Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.activeStaff)}
                                    color="bg-emerald-600"
                                />
                                <KpiCard
                                    icon={UserX}
                                    title="Inactive Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.inactiveStaff)}
                                    color="bg-orange-600"
                                />
                                <KpiCard
                                    icon={XCircle}
                                    title="Exit Staff"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.exitStaff)}
                                    color="bg-rose-600"
                                />
                                <KpiCard
                                    icon={Activity}
                                    title="Present Today"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.presentToday)}
                                    subtitle={`${dashboardData.workforceSnapshot.attendancePercentage}% attendance`}
                                    color="bg-green-500"
                                />
                                <KpiCard
                                    icon={AlertCircle}
                                    title="Absent Today"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.absentToday)}
                                    color="bg-red-500"
                                />
                                <KpiCard
                                    icon={UserMinus}
                                    title="On Leave"
                                    value={formatIndianNumber(dashboardData.workforceSnapshot.onLeave)}
                                    color="bg-purple-500"
                                />
                                <KpiCard
                                    icon={TrendingUp}
                                    title="Attendance %"
                                    value={`${dashboardData.workforceSnapshot.attendancePercentage}%`}
                                    color="bg-indigo-500"
                                />
                            </div>
                        </div>

                        {/* 🟡 Discipline Metrics */}
                        <div className="p-6">
                            <SectionHeader
                                icon={AlertTriangle}
                                title="Discipline Metrics"
                                color="bg-yellow-500"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <KpiCard
                                    icon={AlertCircle}
                                    title="Late Arrivals"
                                    value={formatIndianNumber(dashboardData.disciplineMetrics.lateArrivals)}
                                    color="bg-yellow-500"
                                />
                                <KpiCard
                                    icon={Clock}
                                    title="Early Exits"
                                    value={formatIndianNumber(dashboardData.disciplineMetrics.earlyExits)}
                                    color="bg-orange-500"
                                />
                                <KpiCard
                                    icon={XCircle}
                                    title="Missed Punch"
                                    value={formatIndianNumber(dashboardData.disciplineMetrics.missedPunch)}
                                    color="bg-red-500"
                                />
                                <KpiCard
                                    icon={Clock}
                                    title="Avg Check-In"
                                    value={dashboardData.disciplineMetrics.avgCheckInTime}
                                    color="bg-blue-500"
                                />
                                <KpiCard
                                    icon={Timer}
                                    title="Avg Working Hours"
                                    value={dashboardData.disciplineMetrics.avgWorkingHours}
                                    color="bg-green-500"
                                />
                            </div>
                        </div>

                        {/* 🔵 Work Activity */}
                        <div className="p-6">
                            <SectionHeader
                                icon={Activity}
                                title="Work Activity"
                                color="bg-blue-500"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <KpiCard
                                    icon={Timer}
                                    title="Total Working Hours"
                                    value={dashboardData.workActivity.totalWorkingHours}
                                    color="bg-green-500"
                                />
                                <KpiCard
                                    icon={AlertCircle}
                                    title="Employees < 4 Hours"
                                    value={formatIndianNumber(dashboardData.workActivity.employeesLessThan4Hours)}
                                    color="bg-orange-500"
                                />
                                <KpiCard
                                    icon={Zap}
                                    title="Employees > 9 Hours"
                                    value={formatIndianNumber(dashboardData.workActivity.employeesMoreThan9Hours)}
                                    color="bg-purple-500"
                                />
                                <KpiCard
                                    icon={TrendingUp}
                                    title="Overtime Count"
                                    value={formatIndianNumber(dashboardData.workActivity.overtimeCount)}
                                    color="bg-indigo-500"
                                />
                                <KpiCard
                                    icon={UserX}
                                    title="Yet to Check-In"
                                    value={formatIndianNumber(dashboardData.workActivity.employeesYetToCheckIn)}
                                    color="bg-gray-500"
                                />
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default AttendanceKpiDashboard;
